import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FileDown, Check } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useUsersData } from '../../Helpers/UsersContext';
import { useConfigExport } from '../../hooks/superadmin/useConfigExport';
import { CONFIG_SECTIONS } from '../../api/configTransferApi';
import SuperadminBreadcrumb from './SuperadminBreadcrumb';

/**
 * Superadmin page: export this instance's configuration catalogs to a JSON
 * file that can be imported on a new instance's Konfigurationsstatus page.
 */
const ConfigExportPage: React.FC = () => {
    useDocumentTitle('Exportera konfiguration');
    const navigate = useNavigate();
    const { users } = useUsersData();

    // Belt-and-braces guard in addition to the route guard
    const isSuperadmin = useMemo(() => {
        const uid = getAuth().currentUser?.uid;
        if (!uid || !users) return null; // null = still loading
        return users.find(u => u.key === uid)?.superadmin || false;
    }, [users]);

    useEffect(() => {
        if (isSuperadmin === false) {
            navigate('/superadmin');
        }
    }, [isSuperadmin, navigate]);

    const {
        counts,
        loadingCounts,
        selected,
        toggleSection,
        exporting,
        exportSelected,
        warnings,
        error,
        success,
    } = useConfigExport();

    return (
        <>
        <SuperadminBreadcrumb title="Exportera konfiguration" />
        <Container className="py-4" style={{ maxWidth: 700 }}>
            <h1 className="page-title mb-4">Exportera konfiguration</h1>

            <Card>
                <Card.Header className="d-flex align-items-center gap-2">
                    <FileDown size={18} />
                    Konfigurationsexport
                </Card.Header>
                <Card.Body className="p-4">
                    <p className="text-muted mb-3">
                        Exporterar valda konfigurationssektioner till en JSON-fil. Filen laddas
                        upp på sidan Konfigurationsstatus i en ny instans för att sätta upp den
                        med samma konfiguration. Byggnader, användare och varumärkesanpassning
                        ingår inte.
                    </p>

                    {error && <Alert variant="danger">{error}</Alert>}

                    {success && (
                        <Alert variant="success" className="d-flex align-items-center gap-2">
                            <Check size={18} />
                            Filen har laddats ner
                        </Alert>
                    )}

                    {warnings.length > 0 && (
                        <Alert variant="warning">
                            <ul className="mb-0 ps-3">
                                {warnings.map((warning, i) => <li key={i}>{warning}</li>)}
                            </ul>
                        </Alert>
                    )}

                    {loadingCounts ? (
                        <div className="text-center py-3">
                            <Spinner animation="border" />
                        </div>
                    ) : (
                        <div className="d-flex flex-column gap-2 mb-4">
                            {CONFIG_SECTIONS.map(section => {
                                const count = counts[section.key];
                                const hasData = (count ?? 0) > 0;
                                return (
                                    <Form.Check
                                        key={section.key}
                                        type="checkbox"
                                        id={`export-section-${section.key}`}
                                        checked={selected.has(section.key)}
                                        disabled={!hasData}
                                        onChange={() => toggleSection(section.key)}
                                        label={
                                            <span>
                                                <strong>{section.label}</strong>{' '}
                                                <span className="text-muted">
                                                    — {hasData ? `${count} poster` : 'ingen data'}
                                                </span>
                                                <br />
                                                <small className="text-muted">{section.description}</small>
                                            </span>
                                        }
                                    />
                                );
                            })}
                        </div>
                    )}

                    <Button
                        variant="primary"
                        disabled={loadingCounts || exporting || selected.size === 0}
                        onClick={exportSelected}
                    >
                        {exporting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Exporterar...
                            </>
                        ) : (
                            <>
                                <FileDown size={16} className="me-1" />
                                Exportera {selected.size} {selected.size === 1 ? 'sektion' : 'sektioner'}
                            </>
                        )}
                    </Button>
                </Card.Body>
            </Card>
        </Container>
        </>
    );
};

export default ConfigExportPage;
